/**
 * Reactive dependency graph (ADR-0043 F1 · slice 2).
 *
 * Builds, from a set of named "cells" (each a binding with its read-paths, some
 * of which also PRODUCE a state path), the wiring the reactive runtime needs:
 *  - a **topological recompute order** (producers before consumers),
 *  - **transitive propagation**: given a state write, which cells must
 *    re-evaluate — directly AND through every cell those recomputes feed,
 *  - **cycle detection** (a binding that, through the graph, depends on itself).
 *
 * A "cell" is anything that holds a `{{ }}` expression: a widget prop, a query
 * parameter, a `visible_when`, etc. A cell that maps to a piece of state names
 * it in `produces` — e.g. a query `Get_Post` produces `["$queries","Get_Post"]`,
 * an input `Name` produces `["$page","name"]`. Edges run producer → consumer
 * whenever a consumer reads (overlaps) what a producer writes.
 *
 * Pure: no React, no evaluation — just the graph. The evaluator + the React
 * state container are later slices; this decides WHAT to recompute and IN WHAT
 * ORDER, leaving HOW to them.
 */

import { pathsOverlap, type DepPath } from "./deps";

/** One reactive cell: an id, the paths it reads, optionally the path it writes. */
export interface ReactiveCell {
  id: string;
  /** Scope read-paths (from `extractDeps`). Empty = a constant cell. */
  deps: DepPath[];
  /** The state path this cell writes when it recomputes (a query/input/exposed state). */
  produces?: DepPath;
}

export interface ReactiveGraph {
  /** Cell ids in a valid recompute order (a producer always precedes its consumers). */
  readonly order: string[];
  /** For each cell, the ids of cells that directly consume what it produces. */
  readonly dependents: ReadonlyMap<string, readonly string[]>;
  /** Strongly-connected groups of size >1 (or a self-feeding cell) — real cycles. */
  readonly cycles: string[][];
  /**
   * The cells (in recompute order) that must re-evaluate when `changed` is
   * written: every cell that reads `changed`, plus everything those recomputes
   * transitively feed. `changed` may be one path or several.
   */
  affected(changed: DepPath | DepPath[]): string[];
}

/** Does any of `deps` overlap `target`? */
function readsPath(deps: DepPath[], target: DepPath): boolean {
  return deps.some((d) => pathsOverlap(d, target));
}

/**
 * Build the reactive graph from the cells. O(n²) edge discovery is fine for a
 * page's cell count (tens–low hundreds); a producer index can come later if a
 * page ever gets pathological.
 */
export function buildGraph(cells: ReactiveCell[]): ReactiveGraph {
  const ids = cells.map((c) => c.id);
  const byId = new Map(cells.map((c) => [c.id, c]));

  // Edges: producer.id -> [consumer.id, …]. A consumer C is fed by a producer P
  // (P !== C, P has a `produces`) when one of C's deps overlaps P.produces.
  const dependents = new Map<string, string[]>();
  const predecessors = new Map<string, string[]>();
  for (const id of ids) {
    dependents.set(id, []);
    predecessors.set(id, []);
  }
  for (const consumer of cells) {
    for (const producer of cells) {
      // A self-pair is allowed ON PURPOSE: a cell that reads what it produces
      // is a genuine feedback loop (it would recompute itself forever) and must
      // surface as a cycle. A normal cell never reads its own output, so it
      // gets no self-edge.
      if (!producer.produces) continue;
      if (readsPath(consumer.deps, producer.produces)) {
        dependents.get(producer.id)!.push(consumer.id);
        predecessors.get(consumer.id)!.push(producer.id);
      }
    }
  }

  const cycles = tarjanCycles(ids, dependents);
  const order = topoOrder(ids, predecessors, dependents);

  function affected(changed: DepPath | DepPath[]): string[] {
    const targets = Array.isArray(changed[0]) ? (changed as DepPath[]) : [changed as DepPath];
    // Seeds: cells that DIRECTLY read any changed path.
    const hit = new Set<string>();
    for (const c of cells) {
      if (targets.some((t) => readsPath(c.deps, t))) hit.add(c.id);
    }
    // Forward closure: a recomputed producer feeds its consumers, recursively.
    const stack = [...hit];
    while (stack.length) {
      const n = stack.pop()!;
      for (const m of dependents.get(n) ?? []) {
        if (!hit.has(m)) {
          hit.add(m);
          stack.push(m);
        }
      }
    }
    // Return in recompute order. Cells caught in a cycle (absent from `order`)
    // are appended after, so they still recompute (the runtime caps iterations).
    const seen = new Set<string>();
    const out: string[] = [];
    for (const id of order) {
      if (hit.has(id)) {
        seen.add(id);
        out.push(id);
      }
    }
    for (const id of ids) if (hit.has(id) && !seen.has(id)) out.push(id);
    return out;
  }

  void byId; // reserved for richer diagnostics later
  return { order, dependents, cycles, affected };
}

/**
 * Kahn's algorithm over the predecessor counts. Cells that never reach
 * in-degree 0 are in a cycle and are EXCLUDED from `order` (they're reported in
 * `cycles`); the acyclic majority still gets a clean order.
 */
function topoOrder(
  ids: string[],
  predecessors: Map<string, string[]>,
  dependents: Map<string, string[]>,
): string[] {
  const indeg = new Map<string, number>();
  for (const id of ids) indeg.set(id, predecessors.get(id)!.length);
  // Seed with in-degree-0 cells, preserving input order for determinism.
  const queue = ids.filter((id) => indeg.get(id) === 0);
  const order: string[] = [];
  while (queue.length) {
    const n = queue.shift()!;
    order.push(n);
    for (const m of dependents.get(n) ?? []) {
      const d = indeg.get(m)! - 1;
      indeg.set(m, d);
      if (d === 0) queue.push(m);
    }
  }
  return order; // length < ids.length ⇒ a cycle swallowed the remainder
}

/**
 * Tarjan's SCC, returning only the groups that are genuine cycles: an SCC with
 * more than one member, or a single cell that feeds itself. Iterative (no
 * recursion) so a deep page can't blow the stack.
 */
function tarjanCycles(ids: string[], dependents: Map<string, string[]>): string[][] {
  let idx = 0;
  const index = new Map<string, number>();
  const low = new Map<string, number>();
  const onStack = new Set<string>();
  const stack: string[] = [];
  const out: string[][] = [];

  for (const start of ids) {
    if (index.has(start)) continue;
    // Iterative DFS: frame = [node, nextChildIndex].
    const work: [string, number][] = [[start, 0]];
    while (work.length) {
      const frame = work[work.length - 1];
      const [v, ci] = frame;
      if (ci === 0) {
        index.set(v, idx);
        low.set(v, idx);
        idx++;
        stack.push(v);
        onStack.add(v);
      }
      const kids = dependents.get(v) ?? [];
      if (ci < kids.length) {
        frame[1]++;
        const w = kids[ci];
        if (!index.has(w)) {
          work.push([w, 0]);
        } else if (onStack.has(w)) {
          low.set(v, Math.min(low.get(v)!, index.get(w)!));
        }
      } else {
        if (low.get(v) === index.get(v)) {
          const comp: string[] = [];
          for (;;) {
            const w = stack.pop()!;
            onStack.delete(w);
            comp.push(w);
            if (w === v) break;
          }
          const selfLoop = comp.length === 1 && (dependents.get(comp[0]) ?? []).includes(comp[0]);
          if (comp.length > 1 || selfLoop) out.push(comp.reverse());
        }
        work.pop();
        if (work.length) {
          const parent = work[work.length - 1][0];
          low.set(parent, Math.min(low.get(parent)!, low.get(v)!));
        }
      }
    }
  }
  return out;
}
